'use client';

import React, { FC } from 'react';
import { TbArrowUpRight, TbCheck } from 'react-icons/tb';

import { Anchor, Box, Card, Grid, GridCol, List, ListItem, Stack, Text, ThemeIcon } from '@mantine/core';

import { Carousel, CarouselSlide } from '@marketing/components/carousel/Carousel';
import { FeaturePreview } from '@marketing/components/feature-preview/FeaturePreview';
import { Section } from '@marketing/components/section/Section';
import { SectionHeader, SectionHeaderProps } from '@marketing/components/section/SectionHeader';
import { Feature } from '@marketing/data/features';

import cx from './FeatureGrid.module.scss';

const FeatureCard: FC<{ feature: Feature; withCard?: boolean }> = ({ feature, withCard }) => {
    const content = (
        <Stack gap="md">
            {feature.preview ? (
                <Anchor href={feature.action?.href} style={{ display: 'block' }} underline="never">
                    <FeaturePreview feature={feature} aspectRatio={16 / 9} />
                </Anchor>
            ) : (
                feature.icon && (
                    <ThemeIcon variant="light" color="cyan.0" size={60} radius="xl" fz={30}>
                        {feature.icon}
                    </ThemeIcon>
                )
            )}

            <Stack gap={6} mt="sm">
                <Text fw={600} fz={{ base: 18, md: 20 }}>
                    {feature.title}
                </Text>
                <Text fz={{ base: 14, md: 15 }} c="dimmed" style={{ lineHeight: 1.6 }}>
                    {feature.description}
                </Text>
                {!!feature.features?.length && (
                    <List
                        spacing={8}
                        size="md"
                        center
                        mt={feature.description ? 'xs' : 0}
                        icon={
                            <ThemeIcon color="cyan.0" variant="light" size="md" radius="xl">
                                <TbCheck strokeWidth={2} />
                            </ThemeIcon>
                        }
                    >
                        {feature.features.map((item, itemIndex) => (
                            <ListItem key={itemIndex} fz={{ base: 14, md: 15 }} c="dimmed" style={{ lineHeight: 1.6 }}>
                                {item}
                            </ListItem>
                        ))}
                    </List>
                )}
                {feature.action && (
                    <Anchor fz={13} fw={500} className={cx.link} mt="auto" href={feature.action.href}>
                        {feature.action.label} <TbArrowUpRight size={14} />
                    </Anchor>
                )}
            </Stack>
        </Stack>
    );

    if (!withCard) return content;

    return (
        <Card
            p="xl"
            shadow="xl"
            h="100%"
            radius="lg"
            style={{ background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)' }}
        >
            {content}
        </Card>
    );
};

const FeatureGrid: FC<
    {
        nbCols?: number;
        children: Feature[];
        bgGradient?: { show?: boolean; position?: [string, string]; size?: string };
        layout?: 'grid' | 'carousel';
        footer?: React.ReactNode;
        'data-with-bg'?: string;
        withCard?: boolean;
        wrapper?: boolean;
    } & SectionHeaderProps
> = ({
    prefix,
    prefixProps,
    title,
    description,
    children,
    bgGradient,
    layout = 'grid',
    footer,
    nbCols = 2,
    'data-with-bg': dataWithBg,
    withCard,
    wrapper = true,
}) => {
    const grid = (
        <Grid gap={withCard ? 30 : 60}>
            {children.map((feature, index) => (
                <GridCol key={index} span={{ base: 12, md: 12 / nbCols }}>
                    <FeatureCard feature={feature} withCard={withCard} />
                </GridCol>
            ))}
        </Grid>
    );

    const content = (
        <Stack gap={60}>
            {title && (
                <SectionHeader prefix={prefix} prefixProps={prefixProps} title={title} description={description} />
            )}

            {layout === 'carousel' ? (
                <>
                    <Box hiddenFrom="sm">{grid}</Box>
                    <Carousel
                        withIndicators
                        slideSize={{ base: '100%', sm: '50%' }}
                        slideGap={40}
                        emblaOptions={{ align: 'start', loop: true, slidesToScroll: 2 }}
                        autoplay={false}
                        visibleFrom="sm"
                    >
                        {children.map((feature, index) => (
                            <CarouselSlide key={index} mb="lg">
                                <FeatureCard feature={feature} withCard={withCard} />
                            </CarouselSlide>
                        ))}
                    </Carousel>
                </>
            ) : (
                grid
            )}

            {footer}
        </Stack>
    );

    return wrapper ? (
        <Section bgGradient={bgGradient} data-with-bg={dataWithBg}>
            {content}
        </Section>
    ) : (
        content
    );
};

export { FeatureGrid };
